import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../services/supabaseClient";

// LOT0F12_ADMIN_COMMERCIAL_CONTROL_TOWER_V1

type AnyRecord = Record<string, any>;

const text = (value: unknown, fallback = ""): string =>
  typeof value === "string" && value.trim() ? value.trim() : fallback;

const num = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0;

async function loadCommercialTower() {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) throw sessionError;

  const token = session?.access_token?.trim();

  if (!token) throw new Error("admin_session_required");

  const result = await supabase.functions.invoke("admin-commercial-control-tower", {
    body: { action: "read" },
    headers: { Authorization: `Bearer ${token}` },
  });

  if (result.error) {
    const data = result.data as AnyRecord | null;
    throw new Error(
      typeof data?.error === "string"
        ? data.error
        : result.error.message || "admin_commercial_control_tower_failed",
    );
  }

  const payload = (result.data || {}) as AnyRecord;

  if (payload.ok !== true) {
    throw new Error(text(payload.error, "admin_commercial_control_tower_failed"));
  }

  return payload;
}

export default function AdminCommercialControlTowerPanel() {
  const [payload, setPayload] = useState<AnyRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [region, setRegion] = useState("all");

  const refresh = async () => {
    setLoading(true);
    setError("");

    try {
      setPayload(await loadCommercialTower());
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "admin_commercial_control_tower_failed",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const counts = payload?.counts || {};
  const funnel = payload?.funnel || {};
  const alerts: AnyRecord[] = payload?.alerts || [];
  const activities: AnyRecord[] = payload?.recent_activity || [];
  const checklist: AnyRecord[] = payload?.checklist || [];
  const regions: AnyRecord[] = payload?.regions || [];

  const visibleRegions = useMemo(
    () =>
      region === "all"
        ? regions
        : regions.filter((item) => text(item.region, "Région non définie") === region),
    [region, regions],
  );

  const kpis = [
    ["Prospects PRP", counts.prp_leads_total],
    ["Candidats", funnel.prp_registered],
    ["Approuvés PRP", funnel.prp_approved],
    ["Actifs PRP", funnel.prp_active],
    ["Onboarding à valider", funnel.merchant_under_review],
    ["Commerces approuvés", counts.businesses_approved],
  ];

  return (
    <div className="space-y-6" data-lot0f12-commercial-control-tower="true">
      <section className="rounded-3xl border border-slate-100 bg-gradient-to-br from-white via-white to-slate-50 p-6 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
          <div>
            <p className="text-xs font-black uppercase tracking-[0.22em] text-slate-500">
              0F-12 · Tour de contrôle
            </p>
            <h2 className="mt-1 text-2xl font-black text-slate-950">
              Pilotage commercial PRP → Commerce
            </h2>
            <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-600">
              Lecture administrateur de tout le tunnel : prospection PRP, candidats,
              invitations, onboarding commerçant, portail Explore actif et fermeture
              de boucle PRP. Cette console est strictement lecture seule.
            </p>
          </div>

          <button
            className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-black text-slate-700"
            onClick={() => void refresh()}
            type="button"
          >
            Actualiser
          </button>
        </div>

        {loading && (
          <div className="mt-6 rounded-2xl border border-slate-100 bg-white p-6 text-center text-sm font-bold text-slate-400">
            Chargement de la tour de contrôle…
          </div>
        )}

        {error && (
          <div className="mt-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">
            {error}
          </div>
        )}

        {payload && (
          <div className="mt-6 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
            {kpis.map(([label, value]) => (
              <div className="rounded-2xl border border-slate-200 bg-white p-4" key={String(label)}>
                <p className="text-3xl font-black text-slate-950">{num(value)}</p>
                <p className="mt-1 text-[10px] font-black uppercase tracking-wide text-slate-500">
                  {String(label)}
                </p>
              </div>
            ))}
          </div>
        )}
      </section>

      {payload && (
        <>
          <section className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_430px]">
            <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <p className="font-black text-slate-950">Tunnel de conversion</p>
                  <p className="mt-1 text-xs font-semibold text-slate-400">
                    Volumes PRP et commerce, lecture seule.
                  </p>
                </div>

                <select
                  className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-black"
                  onChange={(event) => setRegion(event.target.value)}
                  value={region}
                >
                  <option value="all">Toutes les régions</option>
                  {regions.map((item) => (
                    <option key={text(item.region, "Région non définie")} value={text(item.region, "Région non définie")}>
                      {text(item.region, "Région non définie")}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {[
                  ["Nouveaux", funnel.prp_new],
                  ["Contactés", funnel.prp_contacted],
                  ["Candidats", funnel.prp_registered],
                  ["Approuvés PRP", funnel.prp_approved],
                  ["Actifs PRP", funnel.prp_active],
                  ["Onboarding commerçant", funnel.merchant_under_review],
                  ["Invitations créées", funnel.invites_created],
                  ["Invitations consommées", funnel.invites_consumed],
                ].map(([label, value]) => (
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={String(label)}>
                    <p className="text-2xl font-black text-slate-950">{num(value)}</p>
                    <p className="mt-1 text-xs font-black uppercase text-slate-500">{String(label)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
              <p className="font-black text-slate-950">Alertes de pilotage</p>
              <p className="mt-1 text-xs font-semibold text-slate-400">
                Bottlenecks détectés à partir des compteurs.
              </p>

              <div className="mt-4 space-y-3">
                {alerts.length === 0 ? (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4 text-sm font-bold text-emerald-800">
                    Aucun blocage majeur détecté.
                  </div>
                ) : (
                  alerts.map((alert, index) => (
                    <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" key={`${text(alert.code, "alert")}-${index}`}>
                      <p className="text-sm font-black text-amber-950">{text(alert.title, "Alerte")}</p>
                      <p className="mt-1 text-xs font-semibold leading-5 text-amber-800">
                        {text(alert.message, "Action humaine requise.")}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </div>
          </section>

          <section className="grid gap-5 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,1.05fr)]">
            <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
              <p className="font-black text-slate-950">Régions administratives</p>
              <p className="mt-1 text-xs font-semibold text-slate-400">
                Synthèse par territoire.
              </p>

              <div className="mt-4 space-y-3">
                {visibleRegions.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-slate-200 p-6 text-center text-sm font-bold text-slate-400">
                    Aucune région à afficher.
                  </div>
                ) : (
                  visibleRegions.map((item) => (
                    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={text(item.region, "Région non définie")}>
                      <div className="flex items-center justify-between gap-3">
                        <p className="font-black text-slate-900">{text(item.region, "Région non définie")}</p>
                        <span className="rounded-full bg-white px-3 py-1 text-[10px] font-black uppercase text-slate-500">
                          {num(item.prps)} PRP
                        </span>
                      </div>
                      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs font-bold">
                        <div className="rounded-xl bg-white p-2">
                          <p className="text-lg font-black text-slate-950">{num(item.leads)}</p>
                          prospects
                        </div>
                        <div className="rounded-xl bg-white p-2">
                          <p className="text-lg font-black text-amber-800">{num(item.candidates)}</p>
                          candidats
                        </div>
                        <div className="rounded-xl bg-white p-2">
                          <p className="text-lg font-black text-emerald-800">{num(item.active)}</p>
                          actifs
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
              <p className="font-black text-slate-950">Checklist validation production</p>
              <p className="mt-1 text-xs font-semibold text-slate-400">
                Contrôles à valider visuellement après Render, dont Autour de moi → Pipeline.
              </p>

              <div className="mt-4 space-y-2">
                {checklist.map((item, index) => (
                  <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4" key={`${text(item.key, "check")}-${index}`}>
                    <p className="text-sm font-black text-slate-950">{text(item.title, "Validation")}</p>
                    <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                      {text(item.description, "")}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
            <p className="font-black text-slate-950">Dernières activités système</p>
            <p className="mt-1 text-xs font-semibold text-slate-400">
              Événements PRP/commerce importants.
            </p>

            <div className="mt-4 divide-y divide-slate-100">
              {activities.length === 0 ? (
                <div className="p-6 text-center text-sm font-bold text-slate-400">
                  Aucune activité récente.
                </div>
              ) : (
                activities.map((activity, index) => (
                  <div className="py-3" key={`${text(activity.id, "activity")}-${index}`}>
                    <p className="text-sm font-bold text-slate-800">{text(activity.summary, "Activité système")}</p>
                    <p className="mt-1 text-[11px] font-black uppercase tracking-wide text-slate-400">
                      {text(activity.event, "system")} · {text(activity.created_at, "")}
                    </p>
                  </div>
                ))
              )}
            </div>
          </section>

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-xs font-bold leading-5 text-blue-900">
            Console lecture seule : aucune approbation, aucune invitation, aucun courriel,
            aucune publication, aucun paiement et aucune mutation Giftshop/Stripe ne sont
            exécutés depuis cette page. Aucune autorité financière n’est accordée.
          </div>
        </>
      )}
    </div>
  );
}
